import { useLocale, useTranslations } from "@acto/web-common/locales";
import type { Todo } from "@acto/taskpane/core/domain";
import { daysOverdue, startOfToday, todoDueDateLabel } from "@acto/taskpane/field-sales-2/utils/todoDueLabels";
import { TodoListItem } from "@acto/taskpane/field-sales-2/components/patterns/TodoListItem";

export type TodoBucketVariant = "overdue" | "open" | "completed";

export function bucketShowsDividers(variant: TodoBucketVariant): boolean {
  return variant !== "completed";
}

interface TodoRowProps {
  todo: Todo;
  variant: TodoBucketVariant;
  isCompleting: boolean;
  /** Absent for completed rows (their checkbox reverts instead — see `onUncomplete`). */
  onComplete?: () => void;
  /** Reverts a completed todo back to open. Only meaningful for completed rows. */
  onUncomplete?: () => void;
  /** While true, a completed row's revert is pending/committing. */
  isReverting?: boolean;
  onCustomerClick: () => void;
  /** Display name of the assignee (may be an optimistic reassignment target); shows an avatar. */
  assigneeName?: string;
  /** Tapping the row opens its edit sheet. Absent for completed rows. */
  onRowClick?: () => void;
}

/** Derives the display props (kind, labels, overdue text) from a Todo and renders a TodoListItem. */
export function TodoRow({
  todo,
  variant,
  isCompleting,
  onComplete,
  onUncomplete,
  isReverting = false,
  onCustomerClick,
  assigneeName,
  onRowClick,
}: TodoRowProps) {
  const t = useTranslations("fieldSalesTwo.todosList");
  const { locale } = useLocale();

  const kind = todo.isWorkflow ? "workflow" : todo.isNoteBased ? "recap" : "todo";
  const kindLabel = todo.isWorkflow ? t("meta.workflow") : todo.isNoteBased ? t("meta.recap") : t("meta.todo");
  const dueDateLabel = todoDueDateLabel(todo.dueDate, locale);
  // Only label as overdue when it's genuinely ≥1 day past due — guards against a 0- or (in westward
  // timezones) negative-day count rendering a nonsense "0T überfällig" / "-1d overdue" pill.
  const overdueDays = variant === "overdue" && todo.dueDate ? daysOverdue(todo.dueDate, startOfToday()) : 0;
  const overdueLabel = overdueDays > 0 ? t("meta.overdueDays", { count: overdueDays }) : undefined;

  return (
    <TodoListItem
      // Workflow/note-based todos have no title, so fall back to the description.
      title={todo.title?.length === 0 ? (todo.description ?? "") : todo.title}
      isCompleted={variant === "completed"}
      kind={kind}
      kindLabel={kindLabel}
      dueDateLabel={overdueLabel ? undefined : dueDateLabel}
      overdueLabel={overdueLabel}
      customerName={todo.client?.name}
      assigneeName={assigneeName}
      onCustomerClick={onCustomerClick}
      onComplete={onComplete}
      onUncomplete={onUncomplete}
      onRowClick={onRowClick}
      isCompleting={isCompleting}
      isReverting={isReverting}
      completeLabel={t("completeAriaLabel")}
      uncompleteLabel={t("uncompleteAriaLabel")}
    />
  );
}
